import { Button } from '@/components/common/Button';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage
} from '@/components/common/Form';
import { Input, PasswordInput } from '@/components/common/Input';
import { ROUTES } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export default observer(function SignInPage() {
    // hooks
    const { t } = useTranslation();
    const navigate = useNavigate();

    // store
    const { authStore: { signIn } } = useStore();

    // validate schema
    const validateSchema = yup.object().shape({
        phoneNumber: yup.string().required('form_error_validate.required'),
        password: yup.string().required('form_error_validate.required')
    });

    type FormData = yup.InferType<typeof validateSchema>;

    // state
    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        defaultValues: {
            phoneNumber: '',
            password: ''
        }
    });

    const [showPassword, setShowPassword] = useState<boolean>(false);

    // function
    const onSubmit = async (data: FormData) => {
        const { phoneNumber, password } = data;
        const res = await flowResult(signIn({ phoneNumber, password }));
        if (res) {
            navigate(ROUTES.home.href);
        }
    };

    const handleClickForgotpassword = () => {
        navigate(ROUTES.forgotPassword.href);
    };

    return (
        <div className='flex w-full px-12 flex-col justify-center space-y-6 py-6 rounded-xl border bg-card text-card-foreground shadow'>
            <h2 className='title-auth-form text-center'>{t('words_title.sign_in')}</h2>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <div className='space-y-4'>
                        <FormField
                            control={form.control}
                            name='phoneNumber'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>
                                        {t('words_title.phone_number')}
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            type='number'
                                            isTypingOnlyNumber={true}
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='password'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>
                                        {t('words_title.password')}
                                    </FormLabel>
                                    <FormControl>
                                        <PasswordInput
                                            type={!showPassword ? 'password' : 'text'}
                                            onToggleIcon={() => setShowPassword(!showPassword)}
                                            isPreventCopy={true}
                                            isPreventCut={true}
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    <div className='mt-2 text-end underline text-sm cursor-pointer text-link-1' onClick={handleClickForgotpassword}>
                        {t('button.forgot_password')}
                    </div>
                    <div className='text-center mt-8'>
                        <Button type='submit' className='min-w-32'>
                            {t('words_title.sign_in')}
                        </Button>
                    </div>
                </form>
            </Form>
        </div>
    );
});
